import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { useApp, useOptimizely, usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';
import isLightColor from '../../utils/colors/isLightColor';

import styles from './DonateButton.scss';
import { isCharity } from '../../utils/typeGuards';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick = () => {},
  trackingDataPageSection,
  experiments,
  successType,
  label,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  const optimizely = useOptimizely();
  const isMounted = useIsMounted();
  const { showCheckoutModal } = useApp();

  const urlParams = new URLSearchParams(
    isMounted ? window.location.search : '',
  );

  const handleClick = (event: any) => {
    setLoading(true);

    // safety net to allow donations to happen if any ab test code goes wrong
    try {
      const isApplePayAvailable = (window as any).ApplePaySession;

      const cookieBotConsents = (window as any).Cookiebot?.consent;
      const isHeapConsented =
        cookieBotConsents?.marketing && cookieBotConsents?.statistics;
      const decision = optimizely?.decide(
        Experiments.ConsumerFintech.CheckoutConversion.EmbeddedCheckoutF4[
          isApplePayAvailable || !isHeapConsented ? 'excludedFlag' : 'flag'
        ],
      );
      if (
        urlParams.get('embedCheckout') === 'true' ||
        decision?.variationKey === 'b'
      ) {
        setLoading(false);
        showCheckoutModal(`${currentUrl}/donate${buildQueryString(true)}`);
        event.preventDefault();
      }
    } catch (error) {
      // Do nothing
    }

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.donateButtonClick);
    }

    onClick();
  };

  const activeDonateButton = (classNames: string, href: string) => {
    return (
      <a
        className={classNames}
        onClick={handleClick}
        href={href}
        data-qa="donateButton"
      >
        {loading ? (
          <Loader size="small" colour="white" />
        ) : (
          label ?? t('common.donateButton')
        )}
      </a>
    );
  };

  if (!basePageData.data?.page) {
    return activeDonateButton(
      classNames('jg-btn', 'jg-btn--themed', styles.button, className),
      `${currentUrl}/donate`,
    );
  }
  const { status, relationships, branding } = basePageData.data.page;

  const isDonateActive = status === PageStatus.Active;

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const beneficiaryNodes =
    basePageData.data?.page?.relationships?.beneficiaries.nodes;
  const firstBeneficiary =
    beneficiaryNodes && beneficiaryNodes.length > 0
      ? beneficiaryNodes[0]
      : null;
  const isChildrenInNeedPage = Boolean(
    ['fundraising', 'campaign'].includes(pageType) &&
      firstBeneficiary &&
      isCharity(firstBeneficiary) &&
      firstBeneficiary.legacyId === '181821',
  );

  const buttonType =
    branding?.primaryColor &&
    isLightColor(branding.primaryColor) &&
    !isChildrenInNeedPage
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  const buildQueryString = (showEmbeddedCheckout?: boolean) => {
    const urlParams = new URLSearchParams();

    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');

    if (
      trimmedExperiments &&
      trimmedExperiments.length > 0 &&
      trimmedSuccessType
    ) {
      urlParams.append('experiments', trimmedExperiments.join(','));
      urlParams.append('successType', trimmedSuccessType);
    }

    if (showEmbeddedCheckout) {
      urlParams.append('checkoutMode', 'Headless');
    }

    return urlParams.toString() ? `?${urlParams.toString()}` : '';
  };

  const buttonClassNames = classNames(
    'jg-btn',
    buttonType,
    styles.button,
    className,
    {
      [styles.childrenInNeedButton]: isChildrenInNeedPage,
    },
    {
      [styles.childrenInNeedButtonNoBandage]:
        isChildrenInNeedPage && trackingDataPageSection === 'StickySummary',
    },
  );

  const donateLink = isDonateActive ? (
    activeDonateButton(
      classNames(buttonClassNames),
      `${currentUrl}/donate${buildQueryString()}`,
    )
  ) : (
    <>
      <div
        className={classNames(
          'jg-btn',
          'jg-text--link',
          buttonType,
          styles.button,
          className,
          styles.disabled,
        )}
        data-qa="donateButtonDisabled"
      >
        <span aria-hidden>{label ?? t('common.donateButton')}</span>
        <span className="jg-sr-only">{t('common.donateButtonInactive')}</span>
      </div>
    </>
  );

  return !isMultiBeneficiaryCampaign ? donateLink : null;
};

export default DonateButton;
